import { Download, LoaderCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { monthRange } from '../list/list-query';
import {
  EXPENSES_MANAGE,
  EXPENSES_REPORT_READ,
  holds,
  useExpensesPermissions,
} from '../use-expenses-permissions';
import {
  downloadExpensesExport,
  downloadNotesExport,
  type ExportClass,
  type ExportOutcome,
} from './api';

const CLASSES: readonly string[] = ['A1', 'A2', 'P', 'PG', 'PD', 'none'];

/** Gestione and the Revisore download the exports (design §6); the server checks again. */
export function useMayExport(): boolean {
  const permissions = useExpensesPermissions();
  return holds(permissions, EXPENSES_MANAGE) || holds(permissions, EXPENSES_REPORT_READ);
}

function ExportButton({
  label,
  run,
}: {
  readonly label: string;
  readonly run: () => Promise<ExportOutcome>;
}) {
  const { t } = useTranslation('expenses');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const click = async () => {
    setBusy(true);
    setFailed(false);
    const outcome = await run();
    setBusy(false);
    setFailed(!outcome.ok);
  };
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <Button
        type="button"
        variant="outline"
        disabled={busy}
        aria-busy={busy}
        onClick={() => void click()}
      >
        {busy ? <LoaderCircle className="animate-spin" /> : <Download />}
        {label}
      </Button>
      {failed ? (
        <span role="alert" className="text-destructive text-sm">
          {t('exports.failed')}
        </span>
      ) : null}
    </span>
  );
}

/**
 * «Esporta note spese»: the year of the list's month, or every year when it shows all periods;
 * the list's owner when one is chosen.
 */
export function ExportNotesButton({
  period,
  owner,
}: {
  /** `YYYY-MM`, or null for every period. */
  readonly period: string | null;
  readonly owner: string | null;
}) {
  const { t } = useTranslation('expenses');
  if (!useMayExport()) return null;
  const year = period === null ? undefined : Number(period.slice(0, 4));
  return (
    <ExportButton
      label={year === undefined ? t('exports.notesAllYears') : t('exports.notesYear', { year })}
      run={() =>
        downloadNotesExport({
          ...(year === undefined ? {} : { year }),
          ...(owner === null ? {} : { owner: [owner] }),
        })
      }
    />
  );
}

/** «Esporta spese per classe»: the list's month, class and owner. */
export function ExportExpensesButton({
  period,
  owner,
  internalClass,
}: {
  readonly period: string | null;
  readonly owner: string | null;
  readonly internalClass: string | null;
}) {
  const { t } = useTranslation('expenses');
  if (!useMayExport()) return null;
  const range = period === null ? null : monthRange(period);
  const chosen =
    internalClass !== null && CLASSES.includes(internalClass)
      ? (internalClass as ExportClass)
      : undefined;
  return (
    <ExportButton
      label={t('exports.expenses')}
      run={() =>
        downloadExpensesExport({
          ...(chosen === undefined ? {} : { class: chosen }),
          ...(range === null ? {} : range),
          ...(owner === null ? {} : { owner: [owner] }),
        })
      }
    />
  );
}
